// Pure selection state for the Derive multi-subaccount picker. Subaccount
// ids are decimal strings (proto int64 fields decode as strings, not
// bigint — see DeriveSubaccountInfo.subaccountId). No side effects here;
// the hook wrappers in use-derive-onboard.ts own the network calls.

export interface SelectionState {
  /** existing subaccount ids the user chose to link */
  selected: string[]
  /** how many brand-new subaccounts to create alongside them */
  createCount: number
  /**
   * Subaccount every create draws its funding from. '0' leaves the choice to
   * the server, which takes the newest one that can cover it.
   */
  fundingSubaccountId: string
}

/** Wire value for "no explicit choice", matching the proto's int64 default. */
export const SERVER_PICKS_FUNDING = '0'

export function empty(): SelectionState {
  return { selected: [], createCount: 0, fundingSubaccountId: SERVER_PICKS_FUNDING }
}

export function setFundingSubaccount(state: SelectionState, id: string): SelectionState {
  return { ...state, fundingSubaccountId: id }
}

// Whether a subaccount can pay for a create. USDC, not balanceUsd: a
// subaccount holding ETH against borrowed USDC prices well above the amount
// and still has nothing a spot transfer can move, and the venue refuses that
// create outright. An unparseable or absent balance reads as "cannot", so an
// unknown is never offered as the source of a transfer that would fail.
export function canFundCreate(usdcBalance: string, amountUsd: string): boolean {
  // Blank is rejected before Number, which maps '' to 0 — an amount the server
  // did not state would otherwise read as free and mark every subaccount
  // fundable.
  if (usdcBalance.trim() === '' || amountUsd.trim() === '') return false
  const usdc = Number(usdcBalance)
  const amount = Number(amountUsd)
  if (!Number.isFinite(usdc) || !Number.isFinite(amount)) return false
  return usdc >= amount
}

// USDC a batch of creates moves out of its single source. Every create in the
// batch draws `amountUsd` from the same subaccount, so a source that covers
// one does not cover three — `create_funding_total` in derive_subaccounts.rs
// is the same arithmetic on the server. '' when the amount is unknown or the
// count is not a real batch, which canFundCreate then reads as "cannot fund".
export function createFundingTotal(amountUsd: string, createCount: number): string {
  if (amountUsd.trim() === '' || !Number.isInteger(createCount) || createCount < 1) return ''
  const amount = Number(amountUsd)
  if (!Number.isFinite(amount)) return ''
  // USDC carries six decimals; rounding there keeps 0.1 * 3 from rendering as
  // 0.30000000000000004 in copy the customer reads as an amount of money.
  return String(Number((amount * createCount).toFixed(6)))
}

// Whether a listed subaccount's USDC balance is known at all. The server
// leaves it empty when per-subaccount enrichment fails or the id sits past its
// enrichment cap, and an unknown balance is not a zero one: the link handler
// re-prices every owned subaccount at submit time, so an unknown is offered
// rather than greyed out, and the server answers precisely if it cannot fund.
export function hasKnownUsdcBalance(usdcBalance: string): boolean {
  return usdcBalance.trim() !== ''
}

// The subaccount the server will draw from when the user has not chosen one:
// the highest-numbered subaccount whose USDC covers the amount. Mirrors
// `create_source_subaccount` so the picker names the same subaccount the
// backend will actually use. '' when none can, which is what the create
// itself would report.
export function defaultFundingSubaccount(
  subaccounts: { subaccountId: string; usdcBalance: string }[],
  amountUsd: string
): string {
  return subaccounts
    .filter((s) => canFundCreate(s.usdcBalance, amountUsd))
    .reduce(
      (best, s) => (best === '' || BigInt(s.subaccountId) > BigInt(best) ? s.subaccountId : best),
      ''
    )
}

// Subaccounts this user already reached through OrderX, on this wallet. The
// server sets linkedAccountId only for the caller's OWN user (alreadyLinked is
// true for anyone's), so this is exactly the set whose trading a re-registered
// key must keep covering.
export function linkedSubaccountIds(
  subaccounts: { subaccountId: string; linkedAccountId: string }[]
): string[] {
  return subaccounts.filter((s) => s.linkedAccountId !== '').map((s) => s.subaccountId)
}

// The subaccount ids a session key must be registered for to carry out this
// selection. Derive bakes the list into the Action the customer signs and
// `private/edit_session_key` cannot widen it afterwards, so everything the
// connection needs has to be named before signing:
//
//   - what this user already linked on this wallet, or re-registering would
//     silently stop their existing accounts from trading;
//   - what the picker just selected;
//   - the subaccount the creates draw their USDC from, since the key signs
//     that transfer on the source.
//
// Freshly created subaccounts are deliberately absent: they have no id until
// the create returns, so a create always ends in a second registration.
// Numerically sorted so the same selection produces the same Action whatever
// order the picker happened to record it in.
export function registrationScope(
  state: SelectionState,
  subaccounts: { subaccountId: string; usdcBalance: string; linkedAccountId: string }[],
  createAmountUsd: string
): string[] {
  const ids = new Set([...linkedSubaccountIds(subaccounts), ...state.selected])

  if (state.createCount >= 1) {
    const source =
      state.fundingSubaccountId !== SERVER_PICKS_FUNDING
        ? state.fundingSubaccountId
        : defaultFundingSubaccount(
            subaccounts,
            createFundingTotal(createAmountUsd, state.createCount)
          )
    // '' means nothing on the wallet can cover the batch. Naming no source is
    // the honest scope; the create then fails at the server, which prices it
    // properly and says why.
    if (source !== '') ids.add(source)
  }

  return [...ids].sort((a, b) => (BigInt(a) < BigInt(b) ? -1 : BigInt(a) > BigInt(b) ? 1 : 0))
}

// Removal of an already-selected id is always allowed (it can only shrink
// totalSlots). Adding is refused — state returned unchanged — once
// totalSlots is already at maxSlots, since a new selection would push it
// over. This is the capacity guard setCreateCount's clamp relies on: as
// long as selected only ever grows through this function, selected.length
// can never exceed maxSlots on its own.
export function toggleSubaccount(
  state: SelectionState,
  id: string,
  info: { alreadyLinked: boolean },
  maxSlots: number
): SelectionState {
  if (info.alreadyLinked) return state
  if (state.selected.includes(id)) {
    return { ...state, selected: state.selected.filter((existing) => existing !== id) }
  }
  if (totalSlots(state) >= maxSlots) return state
  return { ...state, selected: [...state.selected, id] }
}

// Clamps requested create-count so totalSlots never exceeds maxSlots. This
// only holds the invariant if selected.length is itself already
// capacity-guarded (i.e. built via toggleSubaccount, not constructed
// directly) — a state assembled some other way can still exceed maxSlots
// here, which just clamps createCount to 0 rather than raising. canSubmit
// is the sole authoritative submit gate regardless of how selected/
// createCount were arrived at.
export function setCreateCount(state: SelectionState, n: number, maxSlots: number): SelectionState {
  const maxCreate = Math.max(0, maxSlots - state.selected.length)
  const createCount = Math.min(Math.max(0, n), maxCreate)
  return { ...state, createCount }
}

export function totalSlots(state: SelectionState): number {
  return state.selected.length + state.createCount
}

// True when the initiating OrderX account itself consumes one of the picked
// slots, so that slot is NOT a brand-new sibling account. Mirrors the backend's
// `initiating_takes_a_slot || initiating_relinks` (derive_subaccounts.rs):
//   - `initiatingSubaccountId === '0'` → the account has no subaccount yet, so
//     the first linkable slot is spent on itself;
//   - otherwise it only spends a self-slot when the user re-links its own
//     existing subaccount id (an idempotent recovery re-link, not a sibling).
// `initiatingSubaccountId` is the server-authoritative decimal string from
// DeriveListSubaccountsResponse.initiatingSubaccountId.
export function consumesSelfSlot(state: SelectionState, initiatingSubaccountId: string): boolean {
  if (initiatingSubaccountId === '0') return true
  return state.selected.includes(initiatingSubaccountId)
}

// Authoritative submit gate. `remainingSlots` is the plan-cap headroom for NEW
// sibling accounts; the initiating account's own self-slot (see
// `consumesSelfSlot`) never counts against it — exactly as the backend computes
// `siblings = total_slots - self_slot` before the plan-cap check.
export function canSubmit(
  state: SelectionState,
  remainingSlots: number,
  initiatingSubaccountId: string
): boolean {
  const total = totalSlots(state)
  const siblings = total - (consumesSelfSlot(state, initiatingSubaccountId) ? 1 : 0)
  return total >= 1 && siblings <= remainingSlots
}

// Up to this many assets are named in the picker's currency pill; past it the
// pill shows a count. A v3 standard-margin subaccount lists every collateral
// Derive supports (19 at cutover), which as one pill overflows the dialog.
export const MAX_NAMED_CURRENCIES = 2

// The server's `currency` is a comma-joined list ('' when unenriched).
export function currencyList(currency: string): string[] {
  return currency
    .split(',')
    .map((c) => c.trim())
    .filter((c) => c !== '')
}
